import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { KundeKortService } from '../../services/kunde-kort.service';
import { NotificationService } from '../../shared/notification.service';

const ACTION_LABELS: { [k: string]: string } = {
  'customer.create': 'Create customer',
  'customer.update': 'Update customer',
  'customer.link-parent': 'Invoice to parent',
  'customer.preview': 'Check customer',
  'invoice.send': 'Draft invoice',
  'invoice.attach': 'Attach specification',
  'undo': 'Undo',
};

// Everything the PowerOffice integration changed (powerOfficeLog), one row per
// operator action, with failures highlighted and Undo where it is possible.
@Component({
  selector: 'app-poweroffice-log',
  templateUrl: './poweroffice-log.component.html',
  styleUrls: ['./poweroffice-log.component.css']
})
export class PowerOfficeLogComponent implements OnInit, OnDestroy {
  environment = '';
  actions: any[] = [];
  shown: any[] = [];
  problemsOnly = false;
  loading = false;
  error = '';
  expanded: { [actionId: string]: boolean } = {};
  busy: { [actionId: string]: boolean } = {};

  private names: { [id: string]: string } = {};
  private sub: Subscription;

  constructor(private kundeKortService: KundeKortService, private notifier: NotificationService) { }

  ngOnInit() {
    this.sub = this.kundeKortService.getCustomers().subscribe(list => {
      this.names = {};
      (list as any[]).forEach(k => this.names[k.kundekortId] = k.name);
      this.actions.forEach(a => a.card = this.cardName(a.kundekortId));
    });
    this.load();
  }

  ngOnDestroy() { this.sub?.unsubscribe(); }

  async load() {
    this.loading = true;
    this.error = '';
    try {
      const res = await this.kundeKortService.powerOfficeLog(500);
      this.environment = res.environment;
      const byAction: { [id: string]: any } = {};
      (res.entries || []).forEach(e => {
        const id = e.actionId || e.id;
        const a = byAction[id] = byAction[id] || { actionId: e.actionId, at: e.at, entries: [] };
        a.at = Math.max(a.at, e.at);
        a.entries.push(e);
      });
      this.actions = Object.values(byAction).map((a: any) => {
        const first = a.entries[a.entries.length - 1];
        const failed = a.entries.filter(e => e.ok === false);
        const undoable = a.entries.filter(e => e.undo && !e.undoneAt);
        return {
          ...a,
          action: first.action,
          label: ACTION_LABELS[first.action] || first.action || '(script)',
          who: first.email || first.uid || '—',
          env: first.env,
          kundekortId: first.kundekortId,
          card: this.cardName(first.kundekortId),
          undoOf: first.undoOf,
          failed: failed.length,
          refused: a.entries.some(e => e.kind === 'refused'),
          undone: a.entries.some(e => e.undoneAt),
          canUndo: !!a.actionId && first.action !== 'undo' && undoable.length > 0 && first.env === this.environment,
          rows: a.entries.slice().reverse().map(e => ({ ...e, changes: this.changes(e) })),
        };
      }).sort((x, y) => y.at - x.at);
      this.filter();
    } catch (err: any) {
      this.error = err?.error?.message || err?.message || 'Could not load the PowerOffice log';
    } finally {
      this.loading = false;
    }
  }

  filter() {
    this.shown = this.problemsOnly ? this.actions.filter(a => a.failed || a.refused) : this.actions;
  }

  async undo(a: any) {
    const what = a.rows.filter(r => r.undo && !r.undoneAt).map(r => this.undoText(r)).join('\n• ');
    if (!confirm(`Undo "${a.label}"${a.card ? ' for ' + a.card : ''} in PowerOffice ${this.environment.toUpperCase()}?\n\n• ${what}\n\nNothing is changed if any part is no longer safe to undo.`)) return;
    this.busy[a.actionId] = true;
    try {
      await this.kundeKortService.powerOfficeUndo(a.actionId, this.environment);
      this.notifier.showNotification('success', 'Undone');
      await this.load();
    } catch (err: any) {
      this.notifier.showNotification('error', err?.error?.message || err?.message || 'Undo failed');
    } finally {
      this.busy[a.actionId] = false;
    }
  }

  undoText(r: any): string {
    switch (r.undo?.type) {
      case 'archiveCustomer': return `archive the customer it created (PowerOffice cannot delete customers)`;
      case 'deleteDraft': return `delete draft sales order ${r.undo.salesOrderId}`;
      case 'revertPatch': return `put back ${Object.keys(r.before || {}).join(', ')} on ${r.path}`;
      case 'restoreDoc': return `restore ${Object.keys(r.before || {}).join(', ')} on the card`;
      default: return '';
    }
  }

  private changes(e: any): { field: string, before: any, after: any }[] {
    const keys = Object.keys({ ...(e.before || {}), ...(e.after || {}) });
    return keys.map(k => ({ field: k, before: (e.before || {})[k], after: (e.after || {})[k] }));
  }

  private cardName(id: string): string {
    return id ? (this.names[id] || id) : '';
  }
}
